/**
 * FoundingLightModal — the free-registration flow.
 *
 * Mar 2026 · Michael, on solicitor advice: the 40% first-year
 * Founding Light offer must be available to everyone who registers,
 * NOT gated behind a paid contribution. This modal is the free
 * doorway to that status. It sits above the arrival flow and, once
 * completed, unlocks the "Placing a light" wizard as a purely
 * optional next step.
 *
 * Public endpoint (no login required) — captures email + optional
 * first name. Legally-safe copy is baked in.
 */
import React, { useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const TOK = {
  cream: "#f5ecd9",
  creamReadable: "rgba(245,236,217,0.92)",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.42)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.92)",
  goldMist: "rgba(224,169,122,0.22)",
};

export default function FoundingLightModal({ open, onClose, onJoined }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState("form"); // form · success
  const [err, setErr] = useState(null);

  if (!open) return null;

  const submit = async (ev) => {
    ev?.preventDefault?.();
    setErr(null);
    const e = email.trim().toLowerCase();
    if (!e.includes("@") || e.length < 5) {
      setErr("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/one-million-lights/founding-light-signup`,
        { email: e, name: name.trim() || null },
      );
      // Persist locally so the arrival screen recognises returning visitors.
      try { localStorage.setItem("oml_founding_light", JSON.stringify({ email: e, at: new Date().toISOString() })); } catch (_) {}
      setState("success");
      onJoined && onJoined(r.data);
    } catch (e) {
      setErr("Something went wrong. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-5"
      role="dialog"
      aria-modal="true"
      data-testid="oml-fl-modal"
      style={{
        background: "radial-gradient(ellipse at 50% 30%, rgba(19,31,56,0.85) 0%, rgba(3,7,16,0.94) 60%, rgba(3,7,16,0.98) 100%)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-3xl p-7"
        style={{
          background: "linear-gradient(180deg, rgba(224,169,122,0.08) 0%, rgba(19,31,56,0.35) 100%)",
          border: `1px solid ${TOK.goldMist}`,
          boxShadow: "0 40px 100px rgba(0,0,0,0.6)",
        }}
      >
        {state === "form" ? (
          <form onSubmit={submit}>
            <div
              className="uppercase text-center"
              style={{
                color: TOK.goldSoft,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.6rem",
                letterSpacing: "0.36em",
              }}
            >
              Free registration
            </div>
            <h3
              className="mt-3 italic text-center"
              style={{
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(1.5rem, 5vw, 1.9rem)",
                fontWeight: 300,
                lineHeight: 1.2,
              }}
            >
              Become a Founding Light.
            </h3>
            <p
              className="mt-3 italic text-center leading-relaxed"
              style={{
                color: TOK.creamReadable,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(1rem, 3vw, 1.1rem)",
              }}
            >
              Join One Million Lights before public launch and receive
              <span style={{ color: TOK.gold }}> 40% off your first year of Soulumo </span>
              at launch.
            </p>

            <label
              className="block mt-6 text-[0.6rem] uppercase tracking-[0.28em]"
              style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
            >
              Email address
            </label>
            <input
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value.slice(0, 200))}
              data-testid="oml-fl-email"
              placeholder={`you${String.fromCharCode(64)}example.com`}
              className="mt-2 w-full bg-transparent border-b px-3 py-3 outline-none"
              style={{
                borderColor: TOK.goldMist,
                color: TOK.cream,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "1rem",
              }}
            />

            <label
              className="block mt-5 text-[0.6rem] uppercase tracking-[0.28em]"
              style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
            >
              First name (optional)
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 60))}
              data-testid="oml-fl-name"
              placeholder="What we'll call you"
              className="mt-2 w-full bg-transparent border-b px-3 py-3 outline-none italic"
              style={{
                borderColor: TOK.goldMist,
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "1.05rem",
              }}
            />

            {err && (
              <p
                className="mt-4 text-[0.85rem] italic text-center"
                style={{ color: "rgba(224,122,106,0.95)", fontFamily: '"Cormorant Garamond", serif' }}
              >
                {err}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="oml-fl-submit"
              className="mt-7 w-full rounded-full"
              style={{
                background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
                border: "1px solid #f5e0c5",
                color: "#1a0f05",
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.86rem",
                fontWeight: 700,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                padding: "16px 28px",
                boxShadow: "0 0 30px rgba(224,169,122,0.35)",
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.7 : 1,
              }}
            >
              {busy ? "Joining…" : "Join as a Founding Light — Free"}
            </button>

            <p
              className="mt-4 text-[0.7rem] leading-relaxed text-center"
              style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
            >
              No contribution is required to receive Founding Light status.
              Offer subject to launch availability and Founding Light terms.
            </p>

            <button
              type="button"
              onClick={onClose}
              className="mt-4 w-full text-[0.7rem] uppercase tracking-[0.24em]"
              style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
            >
              Not right now
            </button>
          </form>
        ) : (
          <div className="text-center">
            <div
              className="uppercase"
              style={{
                color: TOK.goldSoft,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.6rem",
                letterSpacing: "0.36em",
              }}
            >
              Welcome, Founding Light
            </div>
            <div
              className="mt-4 italic"
              style={{
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(1.6rem, 5.4vw, 2.1rem)",
                fontWeight: 300,
                lineHeight: 1.2,
              }}
            >
              You're in.
            </div>
            <p
              className="mt-4 italic leading-relaxed"
              style={{
                color: TOK.creamReadable,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(1.02rem, 3.2vw, 1.15rem)",
              }}
            >
              Your Founding Light benefit is reserved. We'll email you
              closer to launch with your 40% first-year Soulumo code.
            </p>
            <div
              className="mt-6 text-[0.7rem] italic"
              style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
            >
              Would you also like to help open Soulumo Foundations for someone else?
              Placing a light is completely optional.
            </div>
            <button
              onClick={onClose}
              data-testid="oml-fl-continue"
              className="mt-6 rounded-full"
              style={{
                background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
                border: "1px solid #f5e0c5",
                color: "#1a0f05",
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.82rem",
                fontWeight: 700,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                padding: "14px 28px",
              }}
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
